import { createHttpService } from '../ApiService';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import {
  mockParentUsers,
  mockParentUsersSummary,
} from '../../__mocks__/users/mockParentUsers';
import {
  mockVisitorUsers,
  mockVisitorUsersSummary,
} from '../../__mocks__/users/mockVisitorUsers';
import {
  mockGuestUsers,
  mockGuestUsersSummary,
} from '../../__mocks__/users/mockGuestUsers';
import {
  mockInternalUsers,
  mockInternalUsersSummary,
} from '../../__mocks__/users/mockInternalUsers';
import {
  ParentUser,
  ParentUserListApiResponse,
  ParentUserParams,
  CreateParentUserRequest,
  CreateMeetingRequest,
  Meeting,
  VisitorUser,
  VisitorUserListApiResponse,
  VisitorUserParams,
  CreateVisitorUserRequest,
  CheckInRequest,
  CheckOutRequest,
  GuestUser,
  GuestUserListApiResponse,
  GuestUserParams,
  CreateGuestUserRequest,
  CreateServiceRequest,
  ServiceRecord,
  UpdateServiceStatusRequest,
  UsersSummary,
  VisitorsSummary,
  UserStatus,
  InternalUser,
  InternalUserParams,
  CreateInternalUserRequest,
  TInternalUserListApiResponse,
  UsersByCategoryParams,
  TUsersByCategoryResponse,
  UserByCategoryItem,
} from './users.api.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useUsersApiService = () => {
  const httpAPI = createHttpService();
  const baseUsersPath = '';
  const withMockData = isAppWithMock();

  // ===================== PARENT USERS =====================

  const getParentUsers = (
    params: ParentUserParams
  ): Promise<ParentUserListApiResponse> => {
    if (withMockData) {
      // Apply filtering for mock data
      let filteredUsers = [...mockParentUsers];

      if (params.status && params.status !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.status === params.status
        );
      }

      if (params.relation && params.relation !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.relation === params.relation
        );
      }

      if (params.searchQuery) {
        const query = params.searchQuery.toLowerCase();
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query) ||
            user.phone.includes(query)
        );
      }

      // Sorting
      if (params.sortBy) {
        filteredUsers.sort((a: any, b: any) => {
          const aValue = a[params.sortBy!];
          const bValue = b[params.sortBy!];
          if (params.sortOrder === 'DESC') {
            return aValue > bValue ? -1 : 1;
          }
          return aValue > bValue ? 1 : -1;
        });
      }

      // Pagination
      const page = Number(params.page) || 1;
      const limit = Number(params.limit) || 20;
      const startIndex = (page - 1) * limit;
      const paginatedUsers = filteredUsers.slice(startIndex, startIndex + limit);
      const totalPages = Math.ceil(filteredUsers.length / limit);

      return Promise.resolve(
        normalizePaginatedResponse<ParentUser>(
          {
            data: paginatedUsers,
            count: filteredUsers.length,
            page,
            limit,
            totalPages,
          },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 20,
        ...(params.status && params.status !== 'all' && { status: params.status }),
        ...(params.relation && params.relation !== 'all' && { relation: params.relation }),
        ...(params.searchQuery && { searchQuery: params.searchQuery }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
        ...(params.instituteId && { instituteId: params.instituteId }),
      },
    };

    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUsersPath}/parents`, requestObj)
      .then((res) =>
        normalizePaginatedResponse<ParentUser>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      )
      .catch((error) => {
        console.error('Error fetching parent users:', error);
        throw error;
      });
  };

  const getParentUserById = (id: string): Promise<ParentUser> => {
    if (withMockData) {
      const user = mockParentUsers.find((user: ParentUser) => user.id === id);
      if (user) {
        return Promise.resolve(user);
      }
      return Promise.reject(new Error('Parent user not found'));
    }

    return httpAPI.users
      .get<ParentUser>(`${baseUsersPath}/parents/${id}`)
      .catch((error) => {
        console.error('Error fetching parent user:', error);
        throw error;
      });
  };

  const createParentUser = (
    data: CreateParentUserRequest
  ): Promise<ParentUser> => {
    if (withMockData) {
      const newUser: ParentUser = {
        id: `parent-${Date.now()}`,
        ...data,
        profilePicture: undefined,
        status: UserStatus.ACTIVE,
        createdDate: new Date().toISOString(),
        lastActive: new Date().toISOString(),
        meetingHistory: [],
      };
      return Promise.resolve(newUser);
    }

    return httpAPI.users
      .post<ParentUser>(`${baseUsersPath}/parents`, data)
      .catch((error) => {
        console.error('Error creating parent user:', error);
        throw error;
      });
  };

  const updateParentUser = (
    id: string,
    data: Partial<CreateParentUserRequest>
  ): Promise<ParentUser> => {
    if (withMockData) {
      const user = mockParentUsers.find((user: ParentUser) => user.id === id);
      if (user) {
        const updatedUser = {
          ...user,
          ...data,
          lastActive: new Date().toISOString(),
        };
        return Promise.resolve(updatedUser);
      }
      return Promise.reject(new Error('Parent user not found'));
    }

    return httpAPI.users
      .patch<ParentUser>(`${baseUsersPath}/parents/${id}`, data)
      .catch((error) => {
        console.error('Error updating parent user:', error);
        throw error;
      });
  };

  const deleteParentUser = (id: string): Promise<{ success: boolean; message: string; id: string }> => {
    if (withMockData) {
      return Promise.resolve({
        success: true,
        message: 'Parent user deleted successfully',
        id,
      });
    }

    return httpAPI.users
      .delete<{ success: boolean; message: string; id: string }>(
        `${baseUsersPath}/parents/${id}`
      )
      .catch((error) => {
        console.error('Error deleting parent user:', error);
        throw error;
      });
  };

  const addMeetingToParent = (
    id: string,
    meeting: CreateMeetingRequest
  ): Promise<Meeting> => {
    if (withMockData) {
      const newMeeting: Meeting = {
        id: `meeting-${Date.now()}`,
        ...meeting,
      };
      return Promise.resolve(newMeeting);
    }

    return httpAPI.users
      .post<Meeting>(`${baseUsersPath}/parents/${id}/meetings`, meeting)
      .catch((error) => {
        console.error('Error adding meeting to parent:', error);
        throw error;
      });
  };

  const getParentsSummary = (
    instituteId?: string
  ): Promise<UsersSummary> => {
    if (withMockData) {
      return Promise.resolve(mockParentUsersSummary);
    }

    const requestObj = instituteId
      ? { params: { instituteId } }
      : undefined;

    return httpAPI.users
      .get<UsersSummary>(`${baseUsersPath}/parents/summary`, requestObj)
      .catch((error) => {
        console.error('Error fetching parents summary:', error);
        throw error;
      });
  };

  // ===================== VISITOR USERS =====================

  const getVisitorUsers = (
    params: VisitorUserParams
  ): Promise<VisitorUserListApiResponse> => {
    if (withMockData) {
      // Apply filtering for mock data
      let filteredUsers = [...mockVisitorUsers];

      if (params.status && params.status !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.status === params.status
        );
      }

      if (params.visitStatus && params.visitStatus !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.visitStatus === params.visitStatus
        );
      }

      if (params.visitDate) {
        filteredUsers = filteredUsers.filter(
          (user) => user.visitDate.startsWith(params.visitDate!)
        );
      }

      if (params.searchQuery) {
        const query = params.searchQuery.toLowerCase();
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query) ||
            user.phone.includes(query)
        );
      }

      // Sorting
      if (params.sortBy) {
        filteredUsers.sort((a: any, b: any) => {
          const aValue = a[params.sortBy!];
          const bValue = b[params.sortBy!];
          if (params.sortOrder === 'desc') {
            return aValue > bValue ? -1 : 1;
          }
          return aValue > bValue ? 1 : -1;
        });
      }

      // Pagination
      const page = Number(params.page) || 1;
      const limit = Number(params.limit) || 20;
      const startIndex = (page - 1) * limit;
      const paginatedUsers = filteredUsers.slice(startIndex, startIndex + limit);
      const totalPages = Math.ceil(filteredUsers.length / limit);

      return Promise.resolve(
        normalizePaginatedResponse<VisitorUser>(
          {
            data: paginatedUsers,
            count: filteredUsers.length,
            page,
            limit,
            totalPages,
          },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 20,
        ...(params.status && params.status !== 'all' && { status: params.status }),
        ...(params.visitStatus && params.visitStatus !== 'all' && { visitStatus: params.visitStatus }),
        ...(params.visitDate && { visitDate: params.visitDate }),
        ...(params.searchQuery && { searchQuery: params.searchQuery }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
        ...(params.instituteId && { instituteId: params.instituteId }),
      },
    };

    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUsersPath}/visitors`, requestObj)
      .then((res) =>
        normalizePaginatedResponse<VisitorUser>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      )
      .catch((error) => {
        console.error('Error fetching visitor users:', error);
        throw error;
      });
  };

  const getVisitorUserById = (visitorId: string): Promise<VisitorUser> => {
    if (withMockData) {
      const user = mockVisitorUsers.find((user: VisitorUser) => user.visitorId === visitorId);
      if (user) {
        return Promise.resolve(user);
      }
      return Promise.reject(new Error('Visitor user not found'));
    }

    return httpAPI.users
      .get<VisitorUser>(`${baseUsersPath}/visitors/${visitorId}`)
      .catch((error) => {
        console.error('Error fetching visitor user:', error);
        throw error;
      });
  };

  const getVisitorUserDetails = (visitorId: string): Promise<VisitorUser> => {
    if (withMockData) {
      const user = mockVisitorUsers.find((user: VisitorUser) => user.id === visitorId);
      if (user) {
        return Promise.resolve(user);
      }
      return Promise.reject(new Error('Visitor user not found'));
    }

    return httpAPI.users
      .get<VisitorUser>(`${baseUsersPath}/visitors/details/${visitorId}`)
      .catch((error) => {
        console.error('Error fetching visitor user details:', error);
        throw error;
      });
  };

  const createVisitorUser = (
    data: CreateVisitorUserRequest
  ): Promise<VisitorUser> => {
    if (withMockData) {
      const newUser: VisitorUser = {
        id: `visitor-${Date.now()}`,
        visitorId: `VIS${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        profilePicture: undefined,
        status: UserStatus.ACTIVE,
        createdDate: new Date().toISOString(),
        lastActive: new Date().toISOString(),
        visitDate: data.visitDate,
        visitTime: data.visitTime,
        visitDuration: data.visitDuration,
        visitPurpose: data.visitPurpose,
        hostPerson: data.hostPerson,
        hostDepartment: data.hostDepartment,
        visitStatus: 'scheduled' as any,
        idDocumentType: data.idDocumentType,
        idDocumentNumber: data.idDocumentNumber,
        securityClearance: data.securityClearance,
        vehicleDetails: data.vehicleDetails,
        visitHistory: [],
        address: data.address ? {
          street: data.address.street1,
          city: data.address.city,
          state: data.address.state,
          zipCode: data.address.pinCode,
          country: data.address.country,
        } : undefined,
        notes: data.remarks,
      };
      return Promise.resolve(newUser);
    }

    const { address, ...rest } = data;
    return httpAPI.users
      .post<VisitorUser>(`${baseUsersPath}/visitors`, {
        ...rest,
        addressStreet1: address.street1,
        ...(address.street2 && { addressStreet2: address.street2 }),
        addressCity: address.city,
        addressState: address.state,
        addressPinCode: address.pinCode,
        addressCountry: address.country,
      })
      .catch((error) => {
        console.error('Error creating visitor user:', error);
        throw error;
      });
  };

  const updateVisitorUser = (
    visitorId: string,
    data: Partial<CreateVisitorUserRequest>
  ): Promise<VisitorUser> => {
    if (withMockData) {
      const user = mockVisitorUsers.find((user: VisitorUser) => user.visitorId === visitorId);
      if (user) {
        const updatedUser: VisitorUser = {
          ...user,
          ...data,
          address: data.address ? {
            street: data.address.street1,
            city: data.address.city,
            state: data.address.state,
            zipCode: data.address.pinCode,
            country: data.address.country,
          } : user.address,
          lastActive: new Date().toISOString(),
        };
        return Promise.resolve(updatedUser);
      }
      return Promise.reject(new Error('Visitor user not found'));
    }

    return httpAPI.users
      .patch<VisitorUser>(`${baseUsersPath}/visitors/${visitorId}`, data)
      .catch((error) => {
        console.error('Error updating visitor user:', error);
        throw error;
      });
  };

  const deleteVisitorUser = (visitorId: string): Promise<{ success: boolean; message: string; id: string }> => {
    if (withMockData) {
      return Promise.resolve({
        success: true,
        message: 'Visitor user deleted successfully',
        id: visitorId,
      });
    }

    return httpAPI.users
      .delete<{ success: boolean; message: string; id: string }>(
        `${baseUsersPath}/visitors/${visitorId}`
      )
      .catch((error) => {
        console.error('Error deleting visitor user:', error);
        throw error;
      });
  };

  const checkInVisitor = (
    visitorId: string,
    data?: CheckInRequest
  ): Promise<VisitorUser> => {
    if (withMockData) {
      const user = mockVisitorUsers.find((user: VisitorUser) => user.id === visitorId);
      if (user) {
        return Promise.resolve({
          ...user,
          visitStatus: 'checked_in' as any,
          checkInTime: data?.checkInTime || new Date().toISOString(),
          visitorBadgeNumber: data?.visitorBadgeNumber,
        });
      }
      return Promise.reject(new Error('Visitor user not found'));
    }

    return httpAPI.users
      .post<VisitorUser>(`${baseUsersPath}/visitors/${visitorId}/check-in`, data || {})
      .catch((error) => {
        console.error('Error checking in visitor:', error);
        throw error;
      });
  };

  const checkOutVisitor = (
    visitorId: string,
    data?: CheckOutRequest
  ): Promise<VisitorUser> => {
    if (withMockData) {
      const user = mockVisitorUsers.find((user: VisitorUser) => user.id === visitorId);
      if (user) {
        return Promise.resolve({
          ...user,
          visitStatus: 'checked_out' as any,
          checkOutTime: data?.checkOutTime || new Date().toISOString(),
        });
      }
      return Promise.reject(new Error('Visitor user not found'));
    }

    return httpAPI.users
      .post<VisitorUser>(`${baseUsersPath}/visitors/${visitorId}/check-out`, data || {})
      .catch((error) => {
        console.error('Error checking out visitor:', error);
        throw error;
      });
  };

  const getVisitorsSummary = (): Promise<VisitorsSummary> => {
    if (withMockData) {
      return Promise.resolve(mockVisitorUsersSummary);
    }

    return httpAPI.users
      .get<VisitorsSummary>(`${baseUsersPath}/visitors/summary`)
      .catch((error) => {
        console.error('Error fetching visitors summary:', error);
        throw error;
      });
  };

  // ===================== GUEST USERS =====================

  const getGuestUsers = (
    params: GuestUserParams
  ): Promise<GuestUserListApiResponse> => {
    if (withMockData) {
      // Apply filtering for mock data
      let filteredUsers = [...mockGuestUsers];

      if (params.guestType && params.guestType !== 'all') {
        filteredUsers = filteredUsers.filter(
          (user) => user.guestType === params.guestType
        );
      }

      if (params.search) {
        const query = params.search.toLowerCase();
        filteredUsers = filteredUsers.filter(
          (user) =>
            user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query) ||
            (user.organization && user.organization.toLowerCase().includes(query))
        );
      }

      if (params.eventId) {
        filteredUsers = filteredUsers.filter(
          (user) => user.eventId === params.eventId
        );
      }

      // Sorting
      if (params.sortBy) {
        filteredUsers.sort((a: any, b: any) => {
          const aValue = a[params.sortBy!];
          const bValue = b[params.sortBy!];
          if (params.sortOrder === 'DESC') {
            return aValue > bValue ? -1 : 1;
          }
          return aValue > bValue ? 1 : -1;
        });
      }

      // Pagination
      const page = Number(params.page) || 1;
      const limit = Number(params.limit) || 10;
      const startIndex = (page - 1) * limit;
      const paginatedUsers = filteredUsers.slice(startIndex, startIndex + limit);
      const totalPages = Math.ceil(filteredUsers.length / limit);

      return Promise.resolve(
        normalizePaginatedResponse<GuestUser>(
          {
            data: paginatedUsers,
            count: filteredUsers.length,
            page,
            limit,
            totalPages,
          },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
        ...(params.guestType && params.guestType !== 'all' && { guestType: params.guestType }),
        ...(params.search && { search: params.search }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
        ...(params.eventId && { eventId: params.eventId }),
        ...(params.instituteId && { instituteId: params.instituteId }),
      },
    };

    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUsersPath}/guests`, requestObj)
      .then((res) =>
        normalizePaginatedResponse<GuestUser>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      )
      .catch((error) => {
        console.error('Error fetching guest users:', error);
        throw error;
      });
  };

  const getGuestUserById = (id: string): Promise<GuestUser> => {
    if (withMockData) {
      const user = mockGuestUsers.find((user: GuestUser) => user.id === id);
      if (user) {
        return Promise.resolve(user);
      }
      return Promise.reject(new Error('Guest user not found'));
    }

    return httpAPI.users
      .get<GuestUser>(`${baseUsersPath}/guests/${id}`)
      .catch((error) => {
        console.error('Error fetching guest user:', error);
        throw error;
      });
  };

  const createGuestUser = (
    data: CreateGuestUserRequest
  ): Promise<GuestUser> => {
    if (withMockData) {
      const newUser: GuestUser = {
        id: `guest-${Date.now()}`,
        userId: `user-${Date.now()}`,
        ...data,
        fullName: `${data.firstName} ${data.middleName ? data.middleName + ' ' : ''}${data.lastName}`,
        phoneNumber: data.phone,
        profilePicture: undefined,
        status: UserStatus.ACTIVE,
        createdDate: new Date().toISOString(),
        lastActive: new Date().toISOString(),
        services: data.services?.map((s, idx) => ({
          id: `service-${Date.now()}-${idx}`,
          ...s,
        })) || [],
        documents: data.documents?.map((d, idx) => ({
          id: `doc-${Date.now()}-${idx}`,
          ...d,
        })) || [],
        address: data.address ? {
          street: data.address.street1,
          city: data.address.city,
          state: data.address.state,
          zipCode: data.address.pinCode,
          country: data.address.country,
        } : undefined,
      };
      return Promise.resolve(newUser);
    }

    const { phone, ...rest } = data;
    return httpAPI.users
      .post<GuestUser>(`${baseUsersPath}/guests`, { ...rest, phoneNumber: phone })
      .catch((error) => {
        console.error('Error creating guest user:', error);
        throw error;
      });
  };

  const updateGuestUser = (
    id: string,
    data: Partial<CreateGuestUserRequest>
  ): Promise<GuestUser> => {
    if (withMockData) {
      const user = mockGuestUsers.find((user: GuestUser) => user.id === id);
      if (user) {
        const updatedUser: GuestUser = {
          ...user,
          firstName: data.firstName || user.firstName,
          lastName: data.lastName || user.lastName,
          middleName: data.middleName !== undefined ? data.middleName : user.middleName,
          email: data.email || user.email,
          phone: data.phone || user.phone,
          guestType: data.guestType || user.guestType,
          organization: data.organization !== undefined ? data.organization : user.organization,
          designation: data.designation !== undefined ? data.designation : user.designation,
          invitedBy: data.invitedBy || user.invitedBy,
          invitationDate: data.invitationDate || user.invitationDate,
          accessLevel: data.accessLevel || user.accessLevel,
          eventId: data.eventId !== undefined ? data.eventId : user.eventId,
          eventName: data.eventName !== undefined ? data.eventName : user.eventName,
          accommodation: data.accommodation || user.accommodation,
          services: user.services, // Keep existing services with IDs
          documents: user.documents, // Keep existing documents with IDs
          address: data.address ? {
            street: data.address.street1,
            city: data.address.city,
            state: data.address.state,
            zipCode: data.address.pinCode,
            country: data.address.country,
          } : user.address,
          lastActive: new Date().toISOString(),
        };
        return Promise.resolve(updatedUser);
      }
      return Promise.reject(new Error('Guest user not found'));
    }

    return httpAPI.users
      .patch<GuestUser>(`${baseUsersPath}/guests/${id}`, data)
      .catch((error) => {
        console.error('Error updating guest user:', error);
        throw error;
      });
  };

  const deleteGuestUser = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.users
      .delete<void>(`${baseUsersPath}/guests/${id}`)
      .catch((error) => {
        console.error('Error deleting guest user:', error);
        throw error;
      });
  };

  const addServiceToGuest = (
    id: string,
    service: CreateServiceRequest
  ): Promise<ServiceRecord> => {
    if (withMockData) {
      const newService: ServiceRecord = {
        id: `service-${Date.now()}`,
        ...service,
      };
      return Promise.resolve(newService);
    }

    return httpAPI.users
      .post<ServiceRecord>(`${baseUsersPath}/guests/${id}/services`, service)
      .catch((error) => {
        console.error('Error adding service to guest:', error);
        throw error;
      });
  };

  const updateServiceStatus = (
    guestId: string,
    serviceId: string,
    data: UpdateServiceStatusRequest
  ): Promise<ServiceRecord> => {
    if (withMockData) {
      return Promise.resolve({
        id: serviceId,
        serviceName: 'Mock Service',
        description: 'Mock Description',
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        status: data.status,
      });
    }

    return httpAPI.users
      .patch<ServiceRecord>(
        `${baseUsersPath}/guests/${guestId}/services/${serviceId}`,
        data
      )
      .catch((error) => {
        console.error('Error updating service status:', error);
        throw error;
      });
  };

  const getGuestsSummary = (): Promise<UsersSummary> => {
    if (withMockData) {
      return Promise.resolve(mockGuestUsersSummary);
    }

    return httpAPI.users
      .get<UsersSummary>(`${baseUsersPath}/guests/summary`)
      .catch((error) => {
        console.error('Error fetching guests summary:', error);
        throw error;
      });
  };

  // ===================== INTERNAL USERS =====================

  const getInternalUsers = (
    params: InternalUserParams
  ): Promise<TInternalUserListApiResponse> => {
    if (withMockData) {
      let filtered = [...mockInternalUsers];
      if (params.search) {
        const s = params.search.toLowerCase();
        filtered = filtered.filter(
          (u) =>
            u.firstName.toLowerCase().includes(s) ||
            u.lastName.toLowerCase().includes(s)
        );
      }
      return Promise.resolve(
        normalizePaginatedResponse<InternalUser>(
          { data: filtered, count: filtered.length, page: params.page ?? 1, limit: params.limit ?? 10 },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        instituteId: params.instituteId,
        page: params.page ?? 1,
        limit: params.limit ?? 10,
        ...(params.search && { search: params.search }),
      },
    };
    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUsersPath}/internal`, requestObj)
      .then((res: any) => normalizePaginatedResponse<InternalUser>(res ?? {}, 'data'));
  };

  const createInternalUser = (
    data: CreateInternalUserRequest
  ): Promise<any> => {
    if (withMockData) {
      const newUser: InternalUser = {
        id: `internal-${Date.now()}`,
        firstName: data.firstName,
        lastName: data.lastName,
        middleName: data.middleName,
        email: data.contact.email,
        phone: `${data.contact.countryCode}${data.contact.phoneNumber}`,
        status: UserStatus.ACTIVE,
        userRole: data.userRole,
        hasStaffRecord: false,
        createdDate: new Date().toISOString(),
        lastActive: new Date().toISOString(),
      };
      return Promise.resolve(newUser);
    }

    const formData = new FormData();
    formData.append('firstName', data.firstName);
    if (data.middleName) formData.append('middleName', data.middleName);
    formData.append('lastName', data.lastName);
    formData.append('gender', data.gender);
    if (data.dateOfBirth) formData.append('dateOfBirth', data.dateOfBirth);
    if (data.status) formData.append('status', data.status);
    formData.append('userName', data.userName);
    formData.append('userPassword', data.userPassword);
    formData.append('userRole', data.userRole);
    formData.append('instituteId', data.instituteId);
    formData.append('createdBy', data.createdBy);
    formData.append('address', JSON.stringify(data.address));
    formData.append('contact', JSON.stringify(data.contact));

    return httpAPI.users.post<any>('', formData, {
      headers: { 'Content-Type': 'multipart/form-data' } as any,
    });
  };

  const getInternalUsersSummary = (): Promise<UsersSummary> => {
    if (withMockData) {
      return Promise.resolve(mockInternalUsersSummary);
    }
    return Promise.resolve(mockInternalUsersSummary);
  };

  const getUsersByCategory = (
    params: UsersByCategoryParams
  ): Promise<TUsersByCategoryResponse> => {
    if (withMockData) {
      return Promise.resolve({ data: [], total: 0, totalPages: 0, page: params.page ?? 1, limit: params.limit ?? 10 });
    }
    return httpAPI.users
      .get<TUsersByCategoryResponse>(`${baseUsersPath}/users/by-category`, {
        params: {
          category: params.category,
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.search ? { search: params.search } : {}),
        },
      })
      .then((res: any) => normalizePaginatedResponse<UserByCategoryItem>(res ?? {}, 'data'));
  };

  return {
    // Parent Users
    getParentUsers,
    getParentUserById,
    createParentUser,
    updateParentUser,
    deleteParentUser,
    addMeetingToParent,
    getParentsSummary,
    // Visitor Users
    getVisitorUsers,
    getVisitorUserById,
    getVisitorUserDetails,
    createVisitorUser,
    updateVisitorUser,
    deleteVisitorUser,
    checkInVisitor,
    checkOutVisitor,
    getVisitorsSummary,
    // Guest Users
    getGuestUsers,
    getGuestUserById,
    createGuestUser,
    updateGuestUser,
    deleteGuestUser,
    addServiceToGuest,
    updateServiceStatus,
    getGuestsSummary,
    // Internal Users
    getInternalUsers,
    createInternalUser,
    getInternalUsersSummary,
    getUsersByCategory,
  };
};
